import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import { Button, Dropdown, Grid, Input, Menu } from 'semantic-ui-react';

import { selectFirstLocation } from '../../Components/DateTime/DateTime';
import { DateRangePicker } from '../../Components/Shared/DatePicker';
import { createSearchConditions } from '../transactionsAPI';
import { TransactionType, isAnyTransactionType, isExpenseTransactionType, isRevenueTransactionType } from '../types';

import styles from '../Transactions.module.scss';

/**
 * @param {object} [initialFilters] display state for a deep link, resolved by the list. The list
 *   issues the matching query itself, so this only has to render the controls in agreement.
 */
export default function TransactionsSearchBar({ onSearch, loading, initialFilters = {} }) {
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState(initialFilters.searchText ?? '');
  const [searchField, setSearchField] = useState(initialFilters.searchField ?? '');
  const [transactionType, setTransactionType] = useState(initialFilters.transactionType ?? TransactionType.ANY);
  const isDate = searchField === 'date';
  const firstLocation = useSelector(selectFirstLocation);
  const timezone = firstLocation?.timezone ?? 'utc';

  return (
    <Grid stackable className={styles.transactionsSearchBar}>
      <Grid.Row columns={2}>
        <Grid.Column textAlign="left">
          <Menu stackable compact data-testid="transactions-filter">
            <Menu.Item
              onClick={() => onFilter(TransactionType.ANY)}
              active={isAnyTransactionType(transactionType)}
              data-testid="all-transactions-filter"
            >
              {t('transactions.filters.allTypeOption')}
            </Menu.Item>
            <Menu.Item
              data-testid="expenses-filter"
              onClick={() => onFilter(TransactionType.EXPENSE)}
              active={isExpenseTransactionType(transactionType)}
            >
              {t('transactions.filters.expensesTypeOption')}
            </Menu.Item>
            <Menu.Item
              data-testid="revenue-filter"
              onClick={() => onFilter(TransactionType.REVENUE)}
              active={isRevenueTransactionType(transactionType)}
            >
              {t('transactions.filters.revenueTypeOption')}
            </Menu.Item>
          </Menu>
        </Grid.Column>

        <Grid.Column textAlign="justified" floated="right">
          {renderSearchInputFields()}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );

  function renderSearchInputFields() {
    return (
      <>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={5} className="noPadding">
              <Dropdown
                data-testid="search-dropdown-filter"
                selection
                clearable
                fluid
                loading={loading}
                placeholder={t('transactions.filters.searchByPlaceholder')}
                options={getSearchDropdownOptions(t)}
                value={searchField}
                onChange={onSearchFieldChange}
              />
            </Grid.Column>
            {!isDate && (
              <Grid.Column textAlign="justified" width={11} className="noPaddingLeft">
                <Input
                  data-testid="search-input-filter"
                  fluid
                  label={<SearchButton />}
                  labelPosition="right"
                  disabled={!searchField}
                  placeholder={getSearchTextPlaceholder(searchField, t)}
                  onKeyPress={onSearchKeyPress}
                  onChange={onSearchTextChange}
                />
              </Grid.Column>
            )}
            {isDate && (
              <>
                <Grid.Column textAlign="justified" width={9} className="noPadding">
                  <DateRangePicker
                    data-testid="date-range-filter"
                    fluid
                    closable
                    className="dateRange"
                    allowSameEndDate
                    value={searchText}
                    onChange={onSearchTextChange}
                  />
                </Grid.Column>
                <Grid.Column width={2} className="noPaddingLeft">
                  <SearchButton fluid />
                </Grid.Column>
              </>
            )}
          </Grid.Row>
        </Grid>
      </>
    );
  }
  function SearchButton(props) {
    return (
      <Button
        icon="search"
        onKeyPress={onSearchKeyPress}
        onClick={onSearchClick}
        loading={loading}
        {...props}
        data-testid="search-button"
      />
    );
  }
  function onSearchKeyPress(e) {
    if (e.key === 'Enter') {
      performSearch();
    }
  }
  function onSearchClick(_e) {
    performSearch();
  }
  function onSearchFieldChange(e, { value }) {
    setSearchField(value);
    if (!value) {
      resetSearch();
    } else if (value === 'date') {
      setSearchText('');
    }
  }
  function onSearchTextChange(e, { value }) {
    setSearchText(value);
  }
  function performSearch(currentTransactionType = null) {
    if (loading) return;
    currentTransactionType = currentTransactionType || transactionType;
    const searchConditions = createSearchConditions(searchField, searchText, currentTransactionType, timezone);
    if (onSearch) onSearch(searchConditions);
  }

  function resetSearch() {
    setSearchText('');
    const searchConditions = createSearchConditions(null, '', transactionType);
    if (onSearch) onSearch(searchConditions);
  }

  function onFilter(type) {
    if (transactionType === type) return;
    setTransactionType(type);
    performSearch(type);
  }
}

function getSearchDropdownOptions(t) {
  return [
    {
      key: 'date',
      text: t('transactions.filters.dateRangeOption'),
      value: 'date',
    },
    {
      key: 'category',
      text: t('transactions.filters.categoryOption'),
      value: 'category',
    },
    {
      key: 'payee',
      text: t('transactions.filters.payeeOption'),
      value: 'payee',
    },
    {
      key: 'payer',
      text: t('transactions.filters.payerOption'),
      value: 'payer',
    },
    {
      key: 'notes',
      text: t('transactions.filters.notesOption'),
      value: 'notes',
    },
  ];
}
function getSearchTextPlaceholder(searchField, t) {
  switch (searchField) {
    case 'category':
      return t('transactions.filters.categorySearchPlaceholder');
    case 'payee':
      return t('transactions.filters.payeeSearchPlaceholder');
    case 'payer':
      return t('transactions.filters.payerSearchPlaceholder');
    case 'notes':
      return t('transactions.filters.notesSearchPlaceholder');
    default:
      return t('transactions.filters.generalSearchPlaceholder');
  }
}
